// Shrink a photo in the browser before it goes to S3.
//
// Camera originals run 4-8 MB each. The home carousel shows them in a
// 224px square, so the full file is downloaded and decoded to throw almost
// all of it away — 28 photos was 112 MB and close to fifteen seconds before
// anything appeared. Resizing here rather than on the server is what the
// direct-to-S3 upload leaves room for: there's no longer a request path the
// bytes pass through that could do it for us.
//
// 1600px is well beyond what the carousel needs, so the same file still
// looks right opened full size on a laptop.

// Comfortably past what a 224px carousel square needs, so a photo opened
// full size on a laptop still looks right.
export const CAROUSEL_MAX_EDGE_PX = 1000;

// Reference photos on a custom request are quoted from, not decorated
// with: the shop is looking at stitch detail and thread shades to price
// the work. 2400px keeps enough of that to judge by while still being
// several times quicker to upload from a phone than the original.
export const REFERENCE_MAX_EDGE_PX = 2400;

const DEFAULT_QUALITY = 0.8;
// WebP is roughly 30% smaller than JPEG at matching quality and is
// supported by every browser this site targets. Falls back to JPEG if a
// canvas hands back something else, which is how a browser without WebP
// encoding declines.
const PREFERRED_TYPE = 'image/webp';
const FALLBACK_TYPE = 'image/jpeg';

/** Load a File into an image element, via an object URL that's always freed. */
const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image.'));
    };
    img.src = url;
  });

/**
 * Return a smaller JPEG of `file`, or the original when shrinking it
 * wouldn't help.
 *
 * Falls back to the original on any failure. A photo that won't fit through
 * a canvas — an exotic format, a browser with the API disabled — should
 * still upload at full size rather than not upload at all.
 */
export const downscaleImage = async (
  file: File,
  maxEdgePx: number = CAROUSEL_MAX_EDGE_PX,
  quality: number = DEFAULT_QUALITY
): Promise<File> => {
  // GIFs are usually animated and a canvas would flatten them to one frame.
  if (file.type === 'image/gif') return file;

  try {
    const img = await loadImage(file);
    const longest = Math.max(img.naturalWidth, img.naturalHeight);
    if (longest <= maxEdgePx) return file;

    const scale = maxEdgePx / longest;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);

    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    let blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, PREFERRED_TYPE, quality)
    );
    // A browser that can't encode WebP silently returns PNG instead, which
    // would be larger than what came in. Ask for JPEG in that case.
    if (!blob || blob.type !== PREFERRED_TYPE) {
      blob = await new Promise<Blob | null>(resolve =>
        canvas.toBlob(resolve, FALLBACK_TYPE, quality)
      );
    }
    // A re-encode that came out bigger is not worth having.
    if (!blob || blob.size >= file.size) return file;

    // The extension has to match the bytes: it's what the backend validates
    // and what it signs a content type for.
    const ext = blob.type === PREFERRED_TYPE ? '.webp' : '.jpg';
    const name = file.name.replace(/\.[^.]+$/, '') + ext;
    return new File([blob], name, { type: blob.type, lastModified: Date.now() });
  } catch {
    return file;
  }
};
